/**
 * Storage URL convention for profile photos:
 *   - Original:  /storage/profiles-photos/{id}/{filename}.jpg.webp
 *   - Sized:     /storage/profiles-photos/{id}/{size}/{filename}.jpg(.webp|.avif)
 *
 * This utility builds a set of sources (jpg + webp + avif) for a list of sizes,
 * so a wrapper component can hand them to OptimizedImage without leaking the
 * backend URL convention into the renderer.
 */

const PROFILE_PHOTO_PATH = /profiles-photos\/(\d+)\//;
const PROFILE_PHOTO_PATH_WITH_SIZE = /profiles-photos\/(\d+)\/\d+\//;
const MODERN_FORMAT_SUFFIX = /\.(webp|avif)$/i;

// Centralized image format switches. Keep AVIF disabled until backend/CDN issues are resolved.
const IMAGE_FORMATS = {
  webp: true,
  avif: false,
};

/**
 * Normalizes the incoming src: strips any .webp/.avif suffix and any existing
 * size segment in the path, returning the canonical base form from which any
 * sized URL can be built.
 *
 * @param {string} src
 * @return {string}
 */
function normalizeSrc(src) {
  return src
    .replace(MODERN_FORMAT_SUFFIX, "")
    .replace(PROFILE_PHOTO_PATH_WITH_SIZE, "profiles-photos/$1/");
}

/**
 * Inserts a `/{size}/` segment into the path and optionally appends a format suffix.
 *
 * @param {string} normalizedSrc — output of normalizeSrc
 * @param {number} size
 * @param {"jpg"|"webp"|"avif"} format
 * @return {string}
 */
function buildUrl(normalizedSrc, size, format) {
  const sized = normalizedSrc.replace(
    PROFILE_PHOTO_PATH,
    `profiles-photos/$1/${size}/`
  );
  return format === "jpg" ? sized : `${sized}.${format}`;
}

/**
 * Builds a srcset string with w-descriptors for a single format.
 *
 * @param {string} normalizedSrc
 * @param {number[]} sizes
 * @param {"jpg"|"webp"|"avif"} format
 * @return {string}
 */
function buildSrcset(normalizedSrc, sizes, format) {
  return sizes
    .map((size) => `${buildUrl(normalizedSrc, size, format)} ${size}w`)
    .join(", ");
}

/**
 * @typedef {Object} PhotoSources
 * @property {string} src         — fallback URL (jpg, smallest size)
 * @property {string} srcset      — jpg srcset with w-descriptors
 * @property {string} webpSrcset  — webp srcset
 * @property {string} avifSrcset  — avif srcset
 */

/**
 * Builds ready-to-use src/srcset strings from a source photo URL and a set
 * of widths. The wrapper component (GalleryPhoto, AvatarPhoto) forwards the
 * result into OptimizedImage via v-bind="sources".
 *
 * @export
 * @param {string} src — original photo URL
 * @param {{ sizes: number[] }} options
 * @return {PhotoSources}
 *
 * @example
 * const sources = buildPhotoSources(
 *   "https://api.app.com/storage/profiles-photos/754/abc.jpg.webp",
 *   { sizes: [160, 240, 304] }
 * );
 * // {
 * //   src: "https://.../754/160/abc.jpg",
 * //   srcset: "https://.../754/160/abc.jpg 160w, .../754/240/abc.jpg 240w, ...",
 * //   webpSrcset: "https://.../754/160/abc.jpg.webp 160w, ...",
 * //   avifSrcset: "https://.../754/160/abc.jpg.avif 160w, ...",
 * // }
 */
export function buildPhotoSources(src, { sizes } = {}) {
  if (!src || !sizes || sizes.length === 0) {
    return { src: "", srcset: "", webpSrcset: "", avifSrcset: "" };
  }

  const normalized = normalizeSrc(src);
  const smallest = sizes[0];

  return {
    src: buildUrl(normalized, smallest, "jpg"),
    srcset: buildSrcset(normalized, sizes, "jpg"),
    webpSrcset: IMAGE_FORMATS.webp
      ? buildSrcset(normalized, sizes, "webp")
      : "",
    avifSrcset: IMAGE_FORMATS.avif
      ? buildSrcset(normalized, sizes, "avif")
      : "",
  };
}
